import React from "react";

import Counter from './Counter';
import LightboxImage from "./LightboxImage";

const AddonOption = ({
    description,
    id,
    image,
    label,
    max,
    selectedAddons,
    setSelectedAddons,
    soldOut,
}) => {
    return (
        <li className={`addon-option${soldOut ? ' addon-option--sold-out' : ''}`}>
            {image 
                ? <LightboxImage
                    className="addon-option__image"
                    src={image}
                    alt={label}
                />
                : <div className="addon-option__image addon-option__image__placeholder" role="presentation">&nbsp;</div>
            }
            <div className="addon-option__copy">
                <h3 className="addon-option__title">{label}</h3>
                <div className="addon-option__description">{description}</div>
            </div>
            <Counter
                valueKey={id}
                valueObject={selectedAddons}
                inputLimit={max}
                setValue={setSelectedAddons}
            />
        </li>
    )
};

const AddonsOptions = ({
    options,
    selectedAddons,
    setSelectedAddons,
}) => {
    return (
        <div className="addons-options">
            <h2 className="addons-options__title">Optional add-ons</h2>
            <ul className="addons-options__list">
                {options.map((option) => (
                    <AddonOption
                        key={option.id}
                        description={option.description}
                        id={option.id}
                        image={option.image}
                        label={option.label}
                        max={option.max}
                        selectedAddons={selectedAddons}
                        setSelectedAddons={setSelectedAddons}
                        soldOut={option.soldOut}
                    />
                ))}
            </ul>
        </div>
    );
}

const AddonsSummary = ({ addons }) => {
    const activeAddons = addons.filter(({ quantity }) => quantity);

    return (
        <div className="addons-summary">
            <h3 className="addons-summary__title">Add-ons</h3>
            {activeAddons.length
                ? (
                    <ul className="addons-summary__list">
                        {addons.map((addon) => (
                            <AddonSummary
                                key={addon.id}
                                label={addon.label}
                                quantity={addon.quantity}
                                totalPrice={addon.totalPrice}
                                unitPrice={addon.unitPrice}
                            />
                        ))}
                    </ul>
                )
                : <p className="addons-summary__empty-message">No add-ons selected.</p>
            }
        </div>
    )
}

const AddonSummary = ({
    label,
    quantity,
    totalPrice,
    unitPrice
}) => {
    if(!quantity) {
        return null;
    }

    const formattedUnitPrice = unitPrice ? unitPrice.toFixed(2) : '';
    const formattedTotalPrice = totalPrice ? totalPrice.toFixed(2): '';

    return (
        <li className="addon-summary">
            <span className="addon-summary__left">
                <span className="addon-summary__title">{label}</span>
                <span className="addon-summary__dash"> &ndash; </span>
                <span className="addon-summary__quantity">{quantity}</span>
                {formattedUnitPrice && <>
                    <span className="addon-summary__times>"> x </span>
                    <span className="addon-summary__currency">$</span>
                    <span className="addon-summary__unit-price">{formattedUnitPrice}</span>
                </>}
            </span>
            <span className="addon-summary__right">
                {formattedTotalPrice && <>
                    <span className="addon-summary__currency">$</span>
                    <span className="addon-summary__total-price">{formattedTotalPrice}</span>
                </>}
            </span>
        </li>
    );
}

const Addons = ({
    addonData,
    addons,
    selectedAddons,
    setSelectedAddons,
}) => {
    const normalizedAddons = addons
        .filter(({ addon_id }) => {
            return !addonData || addonData[addon_id]?.active;
        })
        .map(addon => {
            const {
                addon_description,
                addon_id,
                addon_image,
                addon_label,
                addon_maximum_quantity,
            } = addon;

            const data = addonData ? addonData[addon_id] : {};

            const label = addon_label ? addon_label : data?.name;
            const description = addon_description ? addon_description : data?.description;
            let max = addon_maximum_quantity ? parseInt(addon_maximum_quantity) : undefined;
            let soldOut = false;

            if(data && data.type === 'consumable') {
                if(max) {
                    max = Math.min(max, data.quantity);
                } else {
                    max = data.quantity;
                }

                if(max < 1) {
                    soldOut = true;
                }
            }

            const quantity = selectedAddons[addon_id] || 0;
            const unitPrice = data.price || null;
            const totalPrice = unitPrice && quantity ? unitPrice * quantity : null;

            return {
                id: addon_id,
                description,
                image: addon_image,
                label,
                max,
                quantity,
                totalPrice,
                unitPrice,
                soldOut,
            };
        });
        
    return (
        <div className="addons">
            <AddonsOptions
                options={normalizedAddons}
                selectedAddons={selectedAddons}
                setSelectedAddons={setSelectedAddons}
            />
            <AddonsSummary
                addons={normalizedAddons}
                selectedAddons={selectedAddons}
            />
        </div>
    )
}

export default Addons;